import React, { useState, useCallback, useEffect } from 'react'
import styled from 'styled-components'

import AdminHeader from 'live/components/header/ui/admin/header'
import WebSettingsCard from 'live/components/ui/admin/web-settings-card'
import ExtensionService from 'live/services/extension'
import globalNotification from 'live/services/global-notification'
import i18n from 'live/services/i18n'
import { StyledTableForm } from 'live/settings/styled'
import { useWebSettingsState } from 'live/settings/useWebSettingsState'

import ColorScaleSelector, { getLabel } from '../../components/editor/image-log/ColorScaleSelector'

type ImageLogMappingProps = {
    className?: string
}

const ImageLogMappingForm = (props: ImageLogMappingProps): React.ReactElement => {
    const [data, setData] = useState([])

    const { state, actions } = useWebSettingsState(data)

    const fetchData = useCallback(() => {
        fetch(`/services/plugin-wells-vis/settings/image-log-mappings`)
            .then(r => {
                if (r.ok) {
                    return r.json()
                }

                return Promise.reject(new Error(r.statusText))
            })
            .then(r => {
                setData(r)
                actions.setPersistedData(r)
            })
    }, [actions])

    const onSave = useCallback(() => {
        actions.submit()

        fetch(`/services/plugin-wells-vis/settings/image-log-mappings`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(data)
        })
            .then(r => {
                if (r.ok) {
                    actions.saveSuccess(data)
                    globalNotification.success('Settings successfully saved')
                } else {
                    return Promise.reject(r)
                }
            })
            .catch(async r => actions.setError((await r.json()).message))
    }, [actions, data])

    useEffect(() => {
        fetchData()
    }, [fetchData])

    return (
        <Container className={`admin-content${props.className ? ' ' + props.className : ''}`}>
            <div className="admin-inner">
                <AdminHeader>
                    <h2>{i18n('Image Log Mappings')}</h2>
                </AdminHeader>
                <div className="admin-body admin-form">
                    <div className="row">
                        <div className="col-md-12 form-area">
                            <WebSettingsCard
                                name="reason-options"
                                className="live-feature mb-15 web-settings-card-expand"
                                open={state.open}
                                error={state.submitted ? state.error : null}
                                saved={state.open && state.saved}
                                save={onSave}
                                toggle={actions.toggle}
                                onCancel={() => setData(state.persistedData)}
                            >
                                <div className="live-feature__header">
                                    <h2 className="flex-1">{i18n('Image Log Mappings')}</h2>
                                </div>

                                <StyledTableForm
                                    data={data}
                                    fields={[
                                        { name: 'serviceProvider', label: i18n('Drilling Service Provider'), type: 'text' },
                                        { name: 'imageType', label: i18n('Image Type'), type: 'text' },
                                        { name: 'mnemonic', label: i18n('Mnemonic'), type: 'text' },
                                        {
                                            name: 'colorPattern',
                                            label: i18n('Standard Color Pattern'),
                                            type: 'select',
                                            render: state.open
                                                ? props => (
                                                    <ColorScaleSelector
                                                        {...props}
                                                        noFormGroup
                                                        hideLabel
                                                        onChange={value => props.onChange({ target: { value } })}
                                                    />
                                                )
                                                : props => getLabel(props.value)
                                        }
                                    ].map(field => ({ ...field, disabled: !state.open }))}
                                    onChange={setData}
                                    readonly={!state.open}
                                    allowAddRow={state.open}
                                    allowRemoveRow={state.open}
                                />
                            </WebSettingsCard>
                        </div>
                    </div>
                </div>
            </div>
        </Container>
    )
}

const Container = styled.div`
    .web-settings-card-expand {
        max-width: 950px;
    }
`

ExtensionService.addAdminMenu({
    name: i18n('Image Log Mappings'),
    url: '#/image-log-mapping',
    alias: 'image-log-mapping',
    ico: 'fas fa-table',
    position: 'first-block',
    addRoute: true,
    routeUrl: 'image-log-mapping',
    component: ImageLogMappingForm
})
